import React from "react";

// ======= Inline, self-contained CSS (works without Tailwind) =============


const styles = `
:root {
--bg: #f7f9fc;
--card: #ffffff;
--ink: #0f172a;
--muted: #5b667a;
--border: #e5e9f0;
--brand1: #2563eb; /* indigo-600 */
--brand2: #06b6d4; /* cyan-500 */
--brand3: #f43f5e; /* rose-500 */
}
.resources-wrap { background: var(--bg); color: var(--ink); min-height: 100vh; position: relative; overflow: hidden; }
/* side fades to soften the gutters on ultrawide screens */
.resources-wrap::before, .resources-wrap::after {
content: ""; position: fixed; top: 0; bottom: 0; width: clamp(260px, 24vw, 520px); pointer-events: none; z-index: 0;
}
.resources-wrap::before { left: 0; background: linear-gradient(90deg, rgba(2,6,23,0.09), rgba(2,6,23,0)); }
.resources-wrap::after { right: 0; background: linear-gradient(270deg, rgba(2,6,23,0.09), rgba(2,6,23,0)); }


/* softly animated decorative blobs in the far corners */
.decor { position: fixed; inset: 0; pointer-events: none; z-index: 0; }
.decor::before, .decor::after { content: ""; position: absolute; width: 900px; height: 900px; border-radius: 50%; filter: blur(60px); opacity: .45; }
.decor::before { left: -380px; top: -220px; background: radial-gradient(circle at 40% 40%, rgba(56,189,248,.25), rgba(59,130,246,.12) 60%, transparent 70%); }
.decor::after { right: -380px; bottom: -260px; background: radial-gradient(circle at 60% 60%, rgba(168,85,247,.22), rgba(99,102,241,.12) 60%, transparent 70%); }


.resources-container { max-width: 1200px; margin: 0 auto; padding: 40px 20px 80px; position: relative; z-index: 1; }


/* Hero */
.resources-hero { display: grid; place-items: center; margin-bottom: 32px; }
.resources-hero-box {
background: linear-gradient(180deg, #fff, #f9fbff);
border: 1px solid var(--border);
border-radius: 20px;
padding: 28px 28px 30px;
box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
text-align: center;
max-width: 880px;
width: 100%;
}
.resources-title { font-size: 36px; line-height: 1.15; margin: 0; font-weight: 700; letter-spacing: -0.02em; }
@media (min-width: 768px) { .resources-title { font-size: 44px; } }
.resources-sub { margin-top: 10px; color: var(--muted); font-size: 16px; }
.resources-accent { height: 6px; width: 190px; margin: 18px auto 0; border-radius: 999px; background: linear-gradient(90deg, var(--brand1), var(--brand2), var(--brand3)); }


/* Year section */
.year { font-size: 22px; font-weight: 700; margin: 38px 0 18px; display: flex; align-items: center; gap: 10px; }
.year-dot { width: 10px; height: 10px; border-radius: 50%; background: linear-gradient(135deg, var(--brand1), var(--brand2)); box-shadow: 0 0 0 4px #eaf2ff; }


/* Paper card */
.paper { background: var(--card); border: 1px solid var(--border); border-radius: 16px; padding: 22px 22px; box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05); }
.paper + .paper { margin-top: 14px; }
.paper-title { font-weight: 700; font-size: 20px; line-height: 1.35; margin: 0 0 10px; }
@media (min-width: 768px) { .paper-title { font-size: 22px; } }
.paper-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin: 6px 0 12px; font-size: 14px; color: var(--ink); }
.badge { display: inline-flex; align-items: center; gap: 6px; padding: 6px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; border: 1px solid var(--border); background: #f8fafc; color: var(--ink); }
.badge.brand { background: #eef2ff; color: #3730a3; border-color: #e0e7ff; }
.badge.soft { background: #f1f5f9; color: #334155; }


.paper-body { font-size: 16.5px; line-height: 1.7; color: #374151; }
.paper-body strong { color: var(--ink); }
.citation { margin-top: 12px; font-size: 15px; color: #475569; background: #f8fafc; border: 1px solid var(--border); border-radius: 12px; padding: 12px; }


.paper-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; }
.copy-btn { border: 1px solid var(--border); background: #fff; padding: 8px 10px; border-radius: 10px; cursor: pointer; font-size: 12px; color: #0f172a; }
.copy-btn:hover { background: #f8fafc; }
`;

// ======= Data ============================================================
const PAPERS = [
  {
    year: 2025,
    title:
      "Bridging scales and borders on water availability and use in the transboundary Volta River Basin: A water accounting approach",
    authors:
      "Owusu, A., Akpoti, K., Leh, M., Perera, T., Madushanka, L., Mekonnen, K., ... & Velpuri, N. M.",
    journal: "Journal of Hydrology: Regional Studies",
    volume: "59",
    pages: "102377",
    citation:
      "Owusu, A., Akpoti, K., Leh, M., Perera, T., Madushanka, L., Mekonnen, K., ... & Velpuri, N. M. (2025). Bridging scales and borders on water availability and use in the transboundary Volta River Basin: A water accounting approach. Journal of Hydrology: Regional Studies, 59, 102377.",
  },
  {
    year: 2024,
    title: "Advancing water security in Africa with new high-resolution discharge data",
    authors:
      "Akpoti, K., Velpuri, N. M., Mizukami, N., Kagone, S., Leh, M., Mekonnen, K., ... & Seid, A.",
    journal: "Scientific Data (Nature Portfolio)",
    volume: "11(1)",
    pages: "1195",
    citation:
      "Akpoti, K., Velpuri, N. M., Mizukami, N., Kagone, S., Leh, M., Mekonnen, K., ... & Seid, A. (2024). Advancing water security in Africa with new high-resolution discharge data. Nature Scientific Data, 11(1), 1195.",
  },
  {
    year: 2024,
    title:
      "State of continental discharge estimation and modelling: Challenges and opportunities for Africa",
    authors:
      "Akpoti, K., Mekonnen, K., Leh, M., Owusu, A., Dembélé, M., Tinonetsana, P., ... & Velpuri, N. M.",
    journal: "Hydrological Sciences Journal",
    volume: "",
    pages: "",
    citation:
      "Akpoti, K., Mekonnen, K., Leh, M., Owusu, A., Dembélé, M., Tinonetsana, P., ... & Velpuri, N. M. (2024). State of continental discharge estimation and modelling: Challenges and opportunities for Africa. Hydrological Sciences Journal.",
  },
  {
    year: 2024,
    title:
      "A framework for disaggregating remote sensing cropland into rainfed and irrigated classes at continental scale",
    authors:
      "Owusu, A., Kagone, S., Leh, M., Velpuri, N.M., Gumma, M.K., Ghansah, B., Thilina-Prabhath, P., Akpoti, K., Mekonnen, K., Tinonetsana, P., and Mohammed, I.",
    journal:
      "International Journal of Applied Earth Observation and Geoinformation",
    volume: "126",
    pages: "103607",
    citation:
      "Owusu, A., Kagone, S., Leh, M., Velpuri, N.M., Gumma, M.K., Ghansah, B., Thilina-Prabhath, P., Akpoti, K., Mekonnen, K., Tinonetsana, P., and Mohammed, I. (2024). A framework for disaggregating remote sensing cropland into rainfed and irrigated classes at continental scale. International Journal of Applied Earth Observation and Geoinformation, 126, 103607.",
  },
  {
    year: 2023,
    title:
      "Accuracy of satellite and reanalysis rainfall estimates over Africa: A multi-scale assessment of eight products for continental applications",
    authors:
      "Mekonnen, K., Velpuri, N.M., Leh, M., Akpoti, K., Owusu, A., Tinonetsana, P., Hamouda, T., Ghansah, B., Paranamana T.P., Munzimi, Y.",
    journal: "Journal of Hydrology: Regional Studies",
    volume: "4",
    pages: "101514",
    citation:
      "Mekonnen, K., Velpuri, N.M., Leh, M., Akpoti, K., Owusu, A., Tinonetsana, P., Hamouda, T., Ghansah, B., Paranamana T.P., Munzimi, Y. (2023). Accuracy of satellite and reanalysis rainfall estimates over Africa: A multi-scale assessment of eight products for continental applications. Journal of Hydrology: Regional Studies, 4, 101514.",
  },
];

// ======= Component =======================================================
export default function Resources() {
const byYear = PAPERS.reduce((acc, p) => { (acc[p.year] ||= []).push(p); return acc; }, {});
const years = Object.keys(byYear).map(Number).sort((a, b) => b - a);
const copy = async (t) => { try { await navigator.clipboard.writeText(t); } catch {} };


return (
<div className="resources-wrap">
<style>{styles}</style>
<div className="decor" aria-hidden="true"></div>
<div className="resources-container">
<div className="resources-hero">
<div className="resources-hero-box">
<h1 className="resources-title">Peer‑Reviewed Papers</h1>
<p className="resources-sub">Curated publications on water resources, hydrology, and Earth observation.</p>
<div className="resources-accent" />
</div>
</div>
{years.map((y) => (
<section key={y}>
<div className="year"><span className="year-dot" />{y}</div>
{(byYear[y] || []).map((p, i) => (
<article key={i} className="paper">
<div className="paper-top">
<h3 className="paper-title">{p.title}</h3>
<button className="copy-btn" onClick={() => copy(p.citation)}>Copy</button>
</div>
<div className="paper-meta">
<span className="badge brand">{p.journal}</span>
{p.volume ? <span className="badge soft">Vol. {p.volume}</span> : null}
{p.pages ? <span className="badge soft">{p.pages}</span> : null}
<span className="badge soft">{p.year}</span>
</div>
<div className="paper-body">
<div><strong>Authors:&nbsp;</strong>{p.authors}</div>
<div className="citation">{p.citation}</div>
</div>
</article>
))}
</section>
))}
</div>
</div>
);
}
